// The global-search type catalog (#1715): what a result type is called, what
// order the groups appear in, and which permission gates it. Shared by the api
// (which filters and ranks) and both SPAs (which render), so a type can never be
// labelled one way in the dropdown and another on the results page.
//
// Adding a type is meant to be cheap: add a row here, add a loader in the api's
// searchIndex.js, and it appears everywhere. The types deliberately left out of
// v1 — gear, files, preplans, lesson plans, requirements, checks, deployments,
// settings rows (#1486), history — all follow that same recipe.

export const SEARCH_TYPES = [
    {
        // The wire id stays `members` (it indexes usersLight, and that's what the
        // permission model calls it), but the LABEL is "Users" — that's what the
        // nav item, the route and the page are called, and search must not invent
        // a second word for the same place.
        id: 'members',
        label: 'Users',
        singular: 'User',
        // Roster fields are visible to any logged-in member (that's what
        // usersLight exists for); the PII-adjacent fields are gated separately
        // by `piiPermission` below rather than hiding the member entirely.
        permissions: null,
        piiPermission: 'users.write'
    },
    {
        id: 'incidents',
        label: 'Incidents',
        singular: 'Incident',
        permissions: ['incidents.read', 'incidents.stats', 'payroll.read']
    },
    {
        id: 'training',
        label: 'Training records',
        singular: 'Training record',
        permissions: ['training.read', 'training.stats', 'payroll.read']
    },
    {
        id: 'apparatus',
        label: 'Apparatus',
        singular: 'Apparatus',
        // Matches firestore.rules — apparatus is readable by any logged-in user.
        permissions: null
    },
    {
        id: 'requirements',
        label: 'Requirements',
        singular: 'Requirement',
        feature: 'requirements',
        permissions: ['requirements.read', 'requirements.write', 'requirements.report', 'requirements.approve']
    },
    {
        id: 'assignments',
        label: 'Assignments',
        singular: 'Assignment',
        feature: 'requirements',
        // Any logged-in member — but scoped to their OWN assignments at query
        // time (see the `acl.uid` filter in routes/search.js). Someone else's
        // outstanding training is not a search result.
        permissions: null
    },
    {
        id: 'gear',
        label: 'Gear',
        singular: 'Gear item',
        permissions: ['gear.read']
    },
    {
        id: 'fuel',
        label: 'Fuel',
        singular: 'Fuel tank',
        feature: 'fuel',
        permissions: ['fuel.read']
    },
    {
        id: 'preplans',
        label: 'Preplans',
        singular: 'Preplan',
        feature: 'preplans',
        permissions: ['preplans.read']
    },
    {
        id: 'publicEducation',
        label: 'Public Education',
        singular: 'Public education event',
        feature: 'publicEducation',
        permissions: ['publicEducation.read']
    },
    {
        id: 'reports',
        label: 'Reports',
        singular: 'Report',
        clientOnly: true,
        permissions: null
    },
    {
        id: 'settings',
        label: 'Settings',
        singular: 'Setting',
        clientOnly: true,
        permissions: null
    },
    {
        id: 'pages',
        label: 'Go to',
        singular: 'Page',
        clientOnly: true,
        permissions: null
    },
    {
        id: 'help',
        label: 'Help articles',
        singular: 'Help article',
        clientOnly: true,
        permissions: null
    }
];

// Types matched entirely in the browser against catalogs the app already holds
// (nav items, the report registry, the settings rail). They cost the backend
// nothing, need no index, and are already permission- and feature-filtered by
// the hooks that build them — so they render on the first keystroke, while the
// api request for the record types is still in flight.
export const CLIENT_SEARCH_TYPES = SEARCH_TYPES.filter(type => type.clientOnly).map(type => type.id);

// The order groups render in, and the tiebreak order for equally-scored hits.
// People before paperwork: a name is the most common thing anyone searches for.
export const SEARCH_TYPE_ORDER = SEARCH_TYPES.map(type => type.id);

export const SEARCH_TYPES_BY_ID = SEARCH_TYPES.reduce((acc, type) => {
    acc[type.id] = type;
    return acc;
}, {});

/** Types the api can serve — i.e. everything except the client-only ones. */
export const SERVER_SEARCH_TYPES = SEARCH_TYPES.filter(type => !type.clientOnly).map(type => type.id);

export const searchTypeLabel = id => SEARCH_TYPES_BY_ID[id]?.label || id;

/**
 * Sort result-type ids into catalog order. Used by both the dropdown and the
 * results page so the groups always appear in the same sequence.
 */
export const sortSearchTypes = ids => [...ids].sort(
    (a, b) => SEARCH_TYPE_ORDER.indexOf(a) - SEARCH_TYPE_ORDER.indexOf(b)
);
